import { useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { qk } from '@/api/hooks';
import type { Build as BuildT } from '@/api/schemas';
import { useLiveStream, type LiveEvent, type StreamStatus } from '@/features/live/useLiveStream';
import { eventBuildId, eventStatus, needsListRefetch, patchFromEvent, patchPages, refreshesDetails, type BuildPatch, type Page } from './buildsLiveModel';

type QueryClient = ReturnType<typeof useQueryClient>;

/**
 * Bridges the SSE `builds` topic into the query cache: a `build.*` event patches the build wherever it is cached
 * (detail + every list page) and invalidates the lists so new builds appear. Returns the stream status for the
 * live dot in the page header.
 */
export function useBuildsLive(projectId?: string): StreamStatus {
  const qc = useQueryClient();
  const { status, events } = useLiveStream(['builds'], 20);
  const seen = useRef<string | null>(null);

  useEffect(() => {
    const newest = events[0];
    if (!newest || newest.id === seen.current) return;
    seen.current = newest.id;
    if (!newest.type.startsWith('build.')) return;
    if (projectId && newest.projectId && newest.projectId !== projectId) return;
    apply(qc, newest);
  }, [events, projectId, qc]);

  return status;
}

/** Patches the cached detail of the build, if there is one; returns whether it was cached. */
function patchDetail(qc: QueryClient, buildId: string, patch: BuildPatch, ev: LiveEvent): boolean {
  const cached = qc.getQueryData<BuildT>(qk.build(buildId));
  if (!cached) return false;
  qc.setQueryData<BuildT>(qk.build(buildId), patch(cached));
  // Status transitions carry new events, checks and artifacts.
  void qc.invalidateQueries({ queryKey: qk.buildEvents(buildId) });
  if (refreshesDetails(eventStatus(ev))) {
    void qc.invalidateQueries({ queryKey: qk.buildChecks(buildId) });
    void qc.invalidateQueries({ queryKey: qk.buildArtifacts(buildId) });
  }
  return true;
}

/** Patches the build in every cached list page; returns whether any list held it. */
function patchLists(qc: QueryClient, patch: BuildPatch): boolean {
  let found = false;
  qc.setQueriesData<InfiniteData<Page>>({ queryKey: ['builds'] }, (old) => {
    if (!old) return old;
    const { pages, touched } = patchPages(old, patch);
    if (touched) found = true;
    return touched ? { ...old, pages } : old;
  });
  return found;
}

function apply(qc: QueryClient, ev: LiveEvent) {
  const buildId = eventBuildId(ev);
  const patch = patchFromEvent(ev, buildId);
  const inDetail = buildId ? patchDetail(qc, buildId, patch, ev) : false;
  const inLists = patchLists(qc, patch);
  if (needsListRefetch(inDetail || inLists, ev)) void qc.invalidateQueries({ queryKey: ['builds'] });
  void qc.invalidateQueries({ queryKey: ['queue'] });
}
